import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';

import { getLatestExecution } from '@/shared/api/execution';
import { TabLabel } from '@/shared/ui/TabLabel';

import type { AssetDetailResponse } from '@/entities/asset-search/domain/types';
import { mapRunMetricsResponseToExecutionModalMetrics, runManagementApi } from '@/entities/run';

import { ArtifactsTab } from '@/widgets/experiment-asset-detail/ArtifactsTab/ArtifactsTab';
import { DatasetTab } from '@/widgets/experiment-asset-detail/DatasetTab/DatasetTab';
import { FeatureSetTab } from '@/widgets/experiment-asset-detail/FeatureSetTab/FeatureSetTab';
import { MetricsTab } from '@/widgets/run/MetricsTab';
import { ParametersTab } from '@/widgets/run/ParametersTab';

import styles from './ExperimentAssetDetailContent.module.css';

type ExperimentAssetDetailContentProps = {
  assetType: 'run' | 'dataset' | 'feature_set' | 'parameter_set' | 'artifact';
  data: AssetDetailResponse;
  runId?: number;
};

export const ExperimentAssetDetailContent: React.FC<ExperimentAssetDetailContentProps> = ({
  assetType,
  data,
  runId,
}) => {
  const isRunLinked =
    assetType === 'dataset'
      ? !!data.dataset?.snapshotId
      : assetType === 'feature_set'
        ? !!data.featureSet?.snapshotId
        : !!data.run;

  const [currentTab, setCurrentTab] = useState<string>('');

  const { data: executionData } = useQuery({
    queryKey: ['execution', runId],
    queryFn: () => (runId ? getLatestExecution(runId) : null),
    enabled: assetType === 'run' && !!runId,
    retry: 3,
  });

  const sourceRunIdRaw = executionData?.sourceRunId ?? executionData?.source_run_id;
  const sourceRunId =
    sourceRunIdRaw != null && String(sourceRunIdRaw).trim() !== ''
      ? String(sourceRunIdRaw).trim()
      : runId
        ? String(runId)
        : undefined;

  const { data: runMetricsData } = useQuery({
    queryKey: ['runMetrics', sourceRunId],
    queryFn: () => runManagementApi.getMetrics(sourceRunId!),
    enabled: !!sourceRunId,
  });

  const chartMetrics = useMemo(() => {
    if (!runMetricsData) return undefined;
    return mapRunMetricsResponseToExecutionModalMetrics(runMetricsData);
  }, [runMetricsData]);

  const paramsArray = useMemo(() => {
    if (!data.parameterSet?.hyperparameters) return [];
    return Object.entries(data.parameterSet.hyperparameters).map(([key, value]) => ({
      key,
      value: String(value),
    }));
  }, [data.parameterSet]);

  const metricsArray = useMemo(() => {
    if (!data.metrics?.final) return [];
    return Object.entries(data.metrics.final).map(([key, value]) => ({
      key,
      value: typeof value === 'number' ? value : '',
    }));
  }, [data.metrics]);

  const paramsCount = paramsArray.length;
  const metricsCount = metricsArray.length;
  const artifactsCount = data.artifacts?.files?.filter((file) => !file.isDir).length || 0;
  const datasetCount = data.dataset ? 1 : 0;
  const featureSetCount = data.featureSet ? 1 : 0;

  const currentAssetContent = useMemo(() => {
    switch (assetType) {
      case 'parameter_set':
        return paramsArray.length > 0 ? (
          <ParametersTab params={paramsArray} shellVariant="sidebar" />
        ) : (
          <div className={styles.empty}>파라미터 정보가 없습니다.</div>
        );
      case 'artifact':
        if (!data.artifacts) return <div className={styles.empty}>산출물 정보가 없습니다.</div>;
        return <ArtifactsTab artifacts={data.artifacts} />;
      case 'dataset':
        if (!data.dataset) return <div className={styles.empty}>데이터셋 정보가 없습니다.</div>;
        return <DatasetTab dataset={data.dataset} isCurrentAssetType={true} />;
      case 'feature_set':
        if (!data.featureSet) return <div className={styles.empty}>피처셋 정보가 없습니다.</div>;
        return <FeatureSetTab featureSet={data.featureSet} isCurrentAssetType={true} />;
      case 'run':
        return null;
      default:
        return null;
    }
  }, [assetType, data, paramsArray]);

  const tabs = useMemo<TabsProps['items']>(() => {
    const allTabs: Record<string, NonNullable<TabsProps['items']>[number]> = {};

    if (data.parameterSet || paramsCount > 0) {
      if (!(isRunLinked && assetType === 'parameter_set')) {
        allTabs.parameters = {
          key: 'parameters',
          label: <TabLabel label="파라미터셋" count={paramsCount} showCount active={currentTab === 'parameters'} />,
          children:
            paramsArray.length > 0 ? (
              <ParametersTab params={paramsArray} />
            ) : (
              <div className={styles.empty}>파라미터 정보가 없습니다.</div>
            ),
        };
      }
    }

    if (data.metrics || metricsCount > 0) {
      allTabs.metrics = {
        key: 'metrics',
        label: <TabLabel label="메트릭" count={metricsCount} showCount active={currentTab === 'metrics'} />,
        children:
          metricsArray.length > 0 ? (
            <MetricsTab metrics={metricsArray} chartMetrics={chartMetrics} />
          ) : (
            <div className={styles.empty}>메트릭 정보가 없습니다.</div>
          ),
      };
    }

    if (data.artifacts) {
      allTabs.artifacts = {
        key: 'artifacts',
        label: <TabLabel label="산출물" count={artifactsCount} showCount active={currentTab === 'artifacts'} />,
        children: <ArtifactsTab artifacts={data.artifacts} />,
      };
    }

    if (data.dataset) {
      if (!(isRunLinked && assetType === 'dataset')) {
        allTabs.dataset = {
          key: 'dataset',
          label: (
            <TabLabel
              label="데이터셋"
              count={datasetCount}
              showCount={datasetCount !== 1}
              active={currentTab === 'dataset'}
            />
          ),
          children: <DatasetTab dataset={data.dataset} isCurrentAssetType={assetType === 'dataset'} />,
        };
      }
    }

    if (data.featureSet) {
      if (!(isRunLinked && assetType === 'feature_set')) {
        allTabs.feature_set = {
          key: 'feature_set',
          label: (
            <TabLabel
              label="피처셋"
              count={featureSetCount}
              showCount={featureSetCount !== 1}
              active={currentTab === 'feature_set'}
            />
          ),
          children: <FeatureSetTab featureSet={data.featureSet} isCurrentAssetType={assetType === 'feature_set'} />,
        };
      }
    }

    const tabOrder: Record<string, string[]> = {
      run: ['dataset', 'feature_set', 'parameters', 'metrics', 'artifacts'],
      dataset: ['dataset', 'feature_set', 'parameters', 'metrics', 'artifacts'],
      feature_set: ['feature_set', 'dataset', 'parameters', 'metrics', 'artifacts'],
      parameter_set: ['parameters', 'dataset', 'feature_set', 'metrics', 'artifacts'],
      artifact: ['artifacts', 'dataset', 'feature_set', 'parameters', 'metrics'],
    };

    const order = tabOrder[assetType] || ['dataset', 'feature_set', 'parameters', 'metrics', 'artifacts'];

    return order.map((key) => allTabs[key]).filter(Boolean);
  }, [
    assetType,
    currentTab,
    data,
    paramsArray,
    metricsArray,
    paramsCount,
    metricsCount,
    artifactsCount,
    datasetCount,
    featureSetCount,
    isRunLinked,
    chartMetrics,
  ]);

  const defaultTabKey = useMemo(() => {
    if (assetType === 'artifact') {
      return 'artifacts';
    }
    return tabs?.[0]?.key?.toString() || 'dataset';
  }, [assetType, tabs]);

  useEffect(() => {
    if (!currentTab && defaultTabKey) {
      setCurrentTab(defaultTabKey);
    }
  }, [currentTab, defaultTabKey]);

  if (!isRunLinked) {
    return <div className={styles.standaloneContent}>{currentAssetContent}</div>;
  }

  if (assetType === 'run' || assetType === 'artifact') {
    return (
      <div className={styles.runLinkedContent}>
        <div className={styles.centerTabs}>
          <Tabs
            activeKey={currentTab}
            onChange={setCurrentTab}
            items={tabs}
            className={styles.tabs}
            destroyOnHidden={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.runLinkedContent}>
      <div className={styles.leftSidebar}>{currentAssetContent}</div>
      <div className={styles.centerTabs}>
        <Tabs
          activeKey={currentTab}
          onChange={setCurrentTab}
          items={tabs}
          className={styles.tabs}
          destroyOnHidden={false}
        />
      </div>
    </div>
  );
};
